import { Fragment, useRef, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { qs, useApi } from '../lib/api';
import { acct, addPeriods, date, money, num, pct, period, todayIso } from '../lib/format';
import { useSubsidiaries } from '../lib/lookups';
import { useSession } from '../lib/session';
import type { ApAging, ArAging, BalanceSheet, BurnRunway, CashFlow, CashForecast, CashPosition, DeferredRevenue, IncomeStatement, ReportLine, RevenueByCustomer, SaasMetrics, Spend, TrialBalance } from '../lib/types';
import { BarChart, HBars, Legend, LineChart } from '../components/charts';
import { Icons } from '../components/icons';
import { Badge, Button, Card, cx, Input, PageHeader, Query, Segmented, Select, Stat, Table, Td, Th } from '../components/ui';

interface ReportDef {
  name: string;
  title: string;
  description: string;
  group: 'Financial statements' | 'Revenue' | 'Spend' | 'Cash';
  params: 'period' | 'range' | 'none' | 'asOf';
  scoped?: boolean;
}

const REPORTS: ReportDef[] = [
  { name: 'income-statement', title: 'Income statement', description: 'Revenue, gross margin, operating expenses and net income by month', group: 'Financial statements', params: 'range', scoped: true },
  { name: 'balance-sheet', title: 'Balance sheet', description: 'Assets, liabilities and equity, with retained earnings and CTA rolled forward', group: 'Financial statements', params: 'period', scoped: true },
  { name: 'cash-flow', title: 'Cash flow statement', description: 'Indirect method, reconciled to the change in cash', group: 'Financial statements', params: 'range', scoped: true },
  { name: 'trial-balance', title: 'Trial balance', description: 'Every account’s debit or credit balance at period end', group: 'Financial statements', params: 'period', scoped: true },
  { name: 'saas-metrics', title: 'SaaS metrics', description: 'MRR waterfall, ARR, NRR and GRR from contract data', group: 'Revenue', params: 'range' },
  { name: 'deferred-revenue', title: 'Deferred revenue', description: 'Roll-forward of billings and recognition, plus remaining performance obligations', group: 'Revenue', params: 'range' },
  { name: 'revenue-by-customer', title: 'Revenue by customer', description: 'Recognized revenue by customer and month, with concentration', group: 'Revenue', params: 'range' },
  { name: 'ar-aging', title: 'AR aging', description: 'Open receivables by customer and days past due', group: 'Revenue', params: 'asOf' },
  { name: 'spend', title: 'Spend analysis', description: 'Expenses by vendor, department or account', group: 'Spend', params: 'range' },
  { name: 'ap-aging', title: 'AP aging', description: 'Open payables by vendor and days past due', group: 'Spend', params: 'asOf' },
  { name: 'cash-forecast', title: '13-week cash forecast', description: 'Collections, scheduled billings, payables and run-rate spend, week by week', group: 'Cash', params: 'none' },
  { name: 'burn-runway', title: 'Burn and runway', description: 'Monthly inflows, outflows, net burn and months of runway', group: 'Cash', params: 'none' },
  { name: 'cash-position', title: 'Cash position', description: 'Cash by account today, in the base currency', group: 'Cash', params: 'none' },
];

const ICON: Record<ReportDef['group'], ReactNode> = {
  'Financial statements': <Icons.Book size={16} />,
  Revenue: <Icons.Receipt size={16} />,
  Spend: <Icons.Bill size={16} />,
  Cash: <Icons.Bank size={16} />,
};

export function ReportsPage() {
  const groups = [...new Set(REPORTS.map((r) => r.group))];
  return (
    <>
      <PageHeader title="Reports" description="Every report reads the live ledger and its materialized balances. Nothing is exported to a warehouse first, so numbers are always current." />
      <div className="space-y-6">
        {groups.map((g) => (
          <div key={g}>
            <h2 className="mb-2 text-[12px] font-semibold tracking-wide text-muted uppercase">{g}</h2>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {REPORTS.filter((r) => r.group === g).map((r) => (
                <Link key={r.name} to={`/reports/${r.name}`} className="group glass rounded-2xl p-4 transition-colors hover:bg-white/85">
                  <div className="flex items-center gap-2 text-brand">
                    {ICON[r.group]}
                    <span className="text-[13.5px] font-semibold text-ink group-hover:text-brand">{r.title}</span>
                  </div>
                  <p className="mt-1.5 text-[12.5px] text-muted">{r.description}</p>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export function ReportPage() {
  const { name } = useParams();
  const def = REPORTS.find((r) => r.name === name);
  const [params, setParams] = useSearchParams();
  const subs = useSubsidiaries();
  const thisPeriod = todayIso().slice(0, 7);
  const lastClosed = addPeriods(thisPeriod, -1);
  const p = {
    period: params.get('period') ?? lastClosed,
    fromPeriod: params.get('fromPeriod') ?? addPeriods(lastClosed, -5),
    toPeriod: params.get('toPeriod') ?? lastClosed,
    subsidiaryId: params.get('subsidiaryId') ?? '',
    compare: params.get('compare') ?? '',
    groupBy: params.get('groupBy') ?? 'vendor',
    asOf: params.get('asOf') ?? '',
  };
  const set = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  const box = useRef<HTMLDivElement>(null);

  if (!def) return <PageHeader title="Unknown report" />;
  const query =
    def.params === 'range'
      ? { fromPeriod: p.fromPeriod, toPeriod: p.toPeriod, subsidiaryId: p.subsidiaryId, compare: def.name === 'income-statement' ? p.compare : undefined, groupBy: def.name === 'spend' ? p.groupBy : undefined }
      : def.params === 'period'
        ? { period: p.period, subsidiaryId: p.subsidiaryId, comparePeriod: def.name === 'balance-sheet' && p.compare ? addPeriods(p.period, p.compare === 'prior_year' ? -12 : -1) : undefined }
        : def.params === 'asOf'
          ? { asOf: p.asOf }
          : {};

  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/reports" className="hover:text-ink">
            ← Reports
          </Link>
        }
        title={def.title}
        description={def.description}
        actions={
          <>
            {def.scoped && subs.list.length > 1 && (
              <Select className="w-56" value={p.subsidiaryId} onChange={(e) => set('subsidiaryId', e.target.value)}>
                <option value="">Consolidated (all entities)</option>
                {subs.list.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.currency})
                  </option>
                ))}
              </Select>
            )}
            {def.params === 'range' && (
              <>
                <Input type="month" className="w-36" value={p.fromPeriod} onChange={(e) => set('fromPeriod', e.target.value)} aria-label="From" />
                <span className="text-muted">–</span>
                <Input type="month" className="w-36" value={p.toPeriod} onChange={(e) => set('toPeriod', e.target.value)} aria-label="To" />
              </>
            )}
            {def.params === 'period' && <Input type="month" className="w-36" value={p.period} onChange={(e) => set('period', e.target.value)} aria-label="Period" />}
            {def.params === 'asOf' && <Input type="date" className="w-36" value={p.asOf || todayIso()} onChange={(e) => set('asOf', e.target.value)} aria-label="As of" />}
            {(def.name === 'income-statement' || def.name === 'balance-sheet') && (
              <Select className="w-40" value={p.compare} onChange={(e) => set('compare', e.target.value)}>
                <option value="">No comparison</option>
                <option value="prior_period">vs prior {def.name === 'income-statement' ? 'period' : 'month'}</option>
                <option value="prior_year">vs prior year</option>
              </Select>
            )}
            {def.name === 'spend' && (
              <Segmented
                value={p.groupBy as 'vendor'}
                onChange={(v) => set('groupBy', v)}
                items={[
                  { key: 'vendor', label: 'Vendor' },
                  { key: 'department', label: 'Department' },
                  { key: 'account', label: 'Account' },
                ]}
              />
            )}
            <Button icon={<Icons.Upload size={14} className="rotate-180" />} onClick={() => box.current && downloadCsv(box.current, `${def.name}.csv`)}>
              CSV
            </Button>
          </>
        }
      />
      <div ref={box}>
        <ReportBody name={def.name} query={query} />
      </div>
    </>
  );
}

function ReportBody({ name, query }: { name: string; query: Record<string, string | undefined> }) {
  const q = useApi<unknown>(`/api/reports/${name}${qs(query)}`);
  return (
    <Query q={q}>
      {(d) => {
        switch (name) {
          case 'income-statement':
            return <IncomeStatementView r={d as IncomeStatement} />;
          case 'balance-sheet':
            return <BalanceSheetView r={d as BalanceSheet} />;
          case 'cash-flow':
            return <CashFlowView r={d as CashFlow} />;
          case 'trial-balance':
            return <TrialBalanceView r={d as TrialBalance} />;
          case 'saas-metrics':
            return <SaasView r={d as SaasMetrics} />;
          case 'deferred-revenue':
            return <DeferredView r={d as DeferredRevenue} />;
          case 'revenue-by-customer':
            return <RevenueByCustomerView r={d as RevenueByCustomer} />;
          case 'ar-aging':
            return <AgingView r={d as ArAging} kind="customer" />;
          case 'ap-aging':
            return <AgingView r={d as ApAging} kind="vendor" />;
          case 'spend':
            return <SpendView r={d as Spend} />;
          case 'cash-forecast':
            return <ForecastView r={d as CashForecast} />;
          case 'burn-runway':
            return <BurnView r={d as BurnRunway} />;
          case 'cash-position':
            return <PositionView r={d as CashPosition} />;
          default:
            return null;
        }
      }}
    </Query>
  );
}

// --- Statements ---------------------------------------------------------------------------------------------------

function ScopeNote({ consolidated, currency }: { consolidated: boolean; currency: string }) {
  return (
    <div className="mb-3 flex items-center gap-2 text-[12px] text-muted">
      <Badge tone={consolidated ? 'brand' : 'neutral'}>{consolidated ? 'Consolidated' : 'Single entity'}</Badge>
      {consolidated ? `Translated to ${currency}: P&L at average rates, balance sheet at closing rates, intercompany eliminated.` : `Amounts in ${currency}.`}
    </div>
  );
}

function IncomeStatementView({ r }: { r: IncomeStatement }) {
  const cols = r.periods;
  const withCols = cols.length > 1;
  const hasCompare = r.sections.some((s) => s.comparisonTotal !== undefined);
  const c = r.currency;
  const subtotalAfter: Record<string, { label: string; v: { amount: number; columns?: Record<string, number>; comparison?: number } }> = {
    cogs: { label: 'Gross profit', v: r.subtotals.grossProfit },
    opex: { label: 'Operating income', v: r.subtotals.operatingIncome },
  };
  const colCount = 1 + (withCols ? cols.length : 0) + 1 + (hasCompare ? 2 : 0);
  const valueCells = (amount: number, columns?: Record<string, number>, comparison?: number, bold?: boolean) => (
    <>
      {withCols && cols.map((p) => <Td key={p} right className={cx(bold && 'font-semibold')}>{acct(columns?.[p] ?? 0, c, { cents: false })}</Td>)}
      <Td right className={cx('bg-paper/60', bold ? 'font-semibold' : 'font-medium')}>
        {acct(amount, c, { cents: false })}
      </Td>
      {hasCompare && (
        <>
          <Td right muted>
            {acct(comparison ?? 0, c, { cents: false })}
          </Td>
          <Td right className={cx(amount - (comparison ?? 0) < 0 ? 'text-bad' : 'text-good')}>
            {acct(amount - (comparison ?? 0), c, { cents: false })}
          </Td>
        </>
      )}
    </>
  );
  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="Revenue" value={money(r.metrics.revenue, c, { compact: true })} />
        <Stat label="Gross margin" value={pct(r.metrics.grossMargin)} sub={money(r.metrics.grossProfit, c, { compact: true })} />
        <Stat label="Operating margin" value={pct(r.metrics.operatingMargin)} tone={r.metrics.operatingIncome < 0 ? 'bad' : 'good'} sub={money(r.metrics.operatingIncome, c, { compact: true })} />
        <Stat label="Net income" value={money(r.metrics.netIncome, c, { compact: true })} tone={r.metrics.netIncome < 0 ? 'bad' : 'good'} sub={`${period(r.fromPeriod, true)} – ${period(r.toPeriod, true)}`} />
        <Stat label="Months" value={String(cols.length)} sub={r.consolidated ? 'Consolidated' : 'Single entity'} />
      </div>
      <ScopeNote consolidated={r.consolidated} currency={c} />
      <Card pad={false}>
        <Table dense>
          <thead>
            <tr>
              <Th>Account</Th>
              {withCols && cols.map((p) => <Th key={p} right>{period(p, true)}</Th>)}
              <Th right className="bg-paper/60">
                Total
              </Th>
              {hasCompare && (
                <>
                  <Th right>Comparison</Th>
                  <Th right>Change</Th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {r.sections.map((s) => (
              <Fragment key={s.key}>
                <tr>
                  <Td colSpan={colCount} className="pt-4 text-[12px] font-semibold tracking-wide text-ink-2 uppercase">
                    {s.title}
                  </Td>
                </tr>
                {s.lines.map((l) => (
                  <tr key={l.accountId ?? l.name} className="hover:bg-paper">
                    <Td className="pl-7!">
                      {l.accountId ? (
                        <Link to={`/accounts/${l.accountId}`} className="hover:text-brand hover:underline">
                          <span className="num text-muted">{l.code}</span> {l.name}
                        </Link>
                      ) : (
                        l.name
                      )}
                    </Td>
                    {valueCells(l.amount, l.columns, l.comparison)}
                  </tr>
                ))}
                <tr className="bg-paper/40">
                  <Td className="font-medium">Total {s.title.toLowerCase()}</Td>
                  {valueCells(s.total, s.totalColumns, s.comparisonTotal, true)}
                </tr>
                {subtotalAfter[s.key] && (
                  <tr className="bg-brand-soft/40">
                    <Td className="font-semibold">{subtotalAfter[s.key]!.label}</Td>
                    {valueCells(subtotalAfter[s.key]!.v.amount, subtotalAfter[s.key]!.v.columns, subtotalAfter[s.key]!.v.comparison, true)}
                  </tr>
                )}
              </Fragment>
            ))}
            <tr className="border-t-2 border-ink bg-brand-soft/60">
              <Td className="text-[14px] font-semibold">Net income</Td>
              {valueCells(r.subtotals.netIncome.amount, r.subtotals.netIncome.columns, (r.subtotals.netIncome as { comparison?: number }).comparison, true)}
            </tr>
          </tbody>
        </Table>
      </Card>
    </>
  );
}

function StatementLines({ lines, currency, compare }: { lines: ReportLine[]; currency: string; compare: boolean }) {
  return (
    <>
      {lines.map((l) => (
        <tr key={l.accountId ?? l.name} className="hover:bg-paper">
          <Td className="pl-7!">
            {l.accountId ? (
              <Link to={`/accounts/${l.accountId}`} className="hover:text-brand hover:underline">
                <span className="num text-muted">{l.code}</span> {l.name}
              </Link>
            ) : (
              <span className="italic">{l.name}</span>
            )}
          </Td>
          <Td right>{acct(l.amount, currency)}</Td>
          {compare && (
            <Td right muted>
              {acct(l.comparison ?? 0, currency)}
            </Td>
          )}
        </tr>
      ))}
    </>
  );
}

function BalanceSheetView({ r }: { r: BalanceSheet }) {
  const c = r.currency;
  const compare = r.sections.some((s) => s.comparisonTotal !== undefined);
  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Total assets" value={money(r.totals.assets, c, { compact: true })} />
        <Stat label="Total liabilities" value={money(r.totals.liabilities, c, { compact: true })} />
        <Stat label="Equity" value={money(r.totals.equity, c, { compact: true })} />
        <Stat label="Balanced" value={r.balanced ? 'Yes' : 'No'} tone={r.balanced ? 'good' : 'bad'} sub={r.balanced ? 'Assets = liabilities + equity' : 'Out of balance'} />
      </div>
      <ScopeNote consolidated={r.consolidated} currency={c} />
      <Card pad={false}>
        <Table dense>
          <thead>
            <tr>
              <Th>As of {period(r.period)}</Th>
              <Th right w="180px">
                {period(r.period, true)}
              </Th>
              {compare && (
                <Th right w="180px">
                  Comparison
                </Th>
              )}
            </tr>
          </thead>
          <tbody>
            {r.sections.map((s) => (
              <Fragment key={s.key}>
                <tr>
                  <Td colSpan={compare ? 3 : 2} className="pt-4 text-[12px] font-semibold tracking-wide text-ink-2 uppercase">
                    {s.title}
                  </Td>
                </tr>
                <StatementLines lines={s.lines} currency={c} compare={compare} />
                <tr className="bg-paper/40">
                  <Td className="font-medium">Total {s.title.toLowerCase()}</Td>
                  <Td right className="font-semibold">
                    {acct(s.total, c)}
                  </Td>
                  {compare && (
                    <Td right muted className="font-semibold">
                      {acct(s.comparisonTotal ?? 0, c)}
                    </Td>
                  )}
                </tr>
              </Fragment>
            ))}
            <tr className="border-t-2 border-ink bg-brand-soft/50">
              <Td className="font-semibold">Total assets</Td>
              <Td right className="font-semibold">
                {acct(r.totals.assets, c)}
              </Td>
              {compare && <Td />}
            </tr>
            <tr className="bg-brand-soft/50">
              <Td className="font-semibold">Total liabilities and equity</Td>
              <Td right className="font-semibold">
                {acct(r.totals.liabilitiesAndEquity, c)}
              </Td>
              {compare && <Td />}
            </tr>
          </tbody>
        </Table>
      </Card>
    </>
  );
}

function CashFlowView({ r }: { r: CashFlow }) {
  const c = r.currency;
  const Section = ({ title, lines, total, extra }: { title: string; lines: ReportLine[]; total: number; extra?: ReactNode }) => (
    <>
      <tr>
        <Td colSpan={2} className="pt-4 text-[12px] font-semibold tracking-wide text-ink-2 uppercase">
          {title}
        </Td>
      </tr>
      {extra}
      <StatementLines lines={lines} currency={c} compare={false} />
      <tr className="bg-paper/40">
        <Td className="font-medium">Net cash from {title.toLowerCase()}</Td>
        <Td right className="font-semibold">
          {acct(total, c)}
        </Td>
      </tr>
    </>
  );
  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Operating" value={money(r.operating.total, c, { compact: true })} tone={r.operating.total < 0 ? 'bad' : 'good'} />
        <Stat label="Investing" value={money(r.investing.total, c, { compact: true })} />
        <Stat label="Financing" value={money(r.financing.total, c, { compact: true })} />
        <Stat label="Reconciles to cash" value={r.reconciles ? 'Yes' : 'No'} tone={r.reconciles ? 'good' : 'bad'} sub={`${money(r.beginningCash, c, { compact: true })} → ${money(r.endingCash, c, { compact: true })}`} />
      </div>
      <ScopeNote consolidated={r.consolidated} currency={c} />
      <Card pad={false}>
        <Table dense>
          <thead>
            <tr>
              <Th>
                {period(r.fromPeriod)} – {period(r.toPeriod)}
              </Th>
              <Th right w="200px">
                Amount
              </Th>
            </tr>
          </thead>
          <tbody>
            <Section
              title="Operating activities"
              lines={r.operating.adjustments}
              total={r.operating.total}
              extra={
                <tr>
                  <Td className="pl-7!">Net income</Td>
                  <Td right>{acct(r.operating.netIncome, c)}</Td>
                </tr>
              }
            />
            <Section title="Investing activities" lines={r.investing.lines} total={r.investing.total} />
            <Section title="Financing activities" lines={r.financing.lines} total={r.financing.total} />
            <tr>
              <Td className="pt-4">Effect of exchange rates on cash</Td>
              <Td right className="pt-4">
                {acct(r.fxEffect, c)}
              </Td>
            </tr>
            <tr className="border-t-2 border-ink bg-brand-soft/50">
              <Td className="font-semibold">Net change in cash</Td>
              <Td right className="font-semibold">
                {acct(r.netChange, c)}
              </Td>
            </tr>
            <tr>
              <Td muted>Cash at beginning of period</Td>
              <Td right muted>
                {acct(r.beginningCash, c)}
              </Td>
            </tr>
            <tr>
              <Td className="font-semibold">Cash at end of period</Td>
              <Td right className="font-semibold">
                {acct(r.endingCash, c)}
              </Td>
            </tr>
          </tbody>
        </Table>
      </Card>
    </>
  );
}

function TrialBalanceView({ r }: { r: TrialBalance }) {
  const c = r.currency;
  return (
    <>
      <div className="mb-3 flex items-center gap-2">
        <Badge tone={r.balanced ? 'good' : 'bad'} dot>
          {r.balanced ? 'Balanced' : 'Out of balance'}
        </Badge>
        <span className="text-[12px] text-muted">
          {r.rows.length} accounts as of the end of {period(r.period)} · {r.consolidated ? 'consolidated' : 'single entity'}
        </span>
      </div>
      <Card pad={false}>
        <Table dense>
          <thead>
            <tr>
              <Th w="90px">Code</Th>
              <Th>Account</Th>
              <Th>Type</Th>
              <Th right>Debit</Th>
              <Th right>Credit</Th>
            </tr>
          </thead>
          <tbody>
            {r.rows.map((x) => (
              <tr key={x.accountId} className="hover:bg-paper">
                <Td className="num text-muted">{x.code}</Td>
                <Td>
                  <Link to={`/accounts/${x.accountId}`} className="hover:text-brand hover:underline">
                    {x.name}
                  </Link>
                </Td>
                <Td muted className="text-[12px]">
                  {x.type.toLowerCase()}
                </Td>
                <Td right>{x.debit ? acct(x.debit, c) : ''}</Td>
                <Td right>{x.credit ? acct(x.credit, c) : ''}</Td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-ink font-semibold">
              <Td colSpan={3}>Total</Td>
              <Td right>{acct(r.totalDebit, c)}</Td>
              <Td right>{acct(r.totalCredit, c)}</Td>
            </tr>
          </tfoot>
        </Table>
      </Card>
    </>
  );
}

// --- Revenue ------------------------------------------------------------------------------------------------------

function SaasView({ r }: { r: SaasMetrics }) {
  const { currency: c } = useSession();
  const last = r.months[r.months.length - 1];
  const labels = r.months.map((m) => period(m.period, true));
  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="Ending MRR" value={money(last?.endingMrr ?? 0, c, { compact: true })} sub={`ARR ${money(last?.arr ?? 0, c, { compact: true })}`} />
        <Stat label="Net revenue retention" value={pct(r.nrr, 0)} tone={r.nrr !== null && r.nrr >= 1 ? 'good' : 'warn'} sub="Trailing, existing customers" />
        <Stat label="Gross revenue retention" value={pct(r.grr, 0)} tone={r.grr !== null && r.grr >= 0.9 ? 'good' : 'warn'} />
        <Stat label="Customers" value={String(last?.customers ?? 0)} />
        <Stat label="ARPA" value={money(last?.arpa ?? 0, c, { compact: true })} sub="Monthly, per account" />
      </div>
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <Card title="MRR movements" subtitle="New and expansion above the line; contraction and churn below">
          <BarChart
            labels={labels}
            stacked
            series={[
              { name: 'New', values: r.months.map((m) => m.new), color: 'var(--color-series-1)' },
              { name: 'Expansion', values: r.months.map((m) => m.expansion), color: 'var(--color-series-2)' },
              { name: 'Contraction', values: r.months.map((m) => m.contraction), color: 'var(--color-series-3)' },
              { name: 'Churn', values: r.months.map((m) => m.churn), color: 'var(--color-bad)' },
            ]}
            format={(v) => money(v, c)}
            axisFormat={(v) => money(v, c, { compact: true })}
            height={220}
          />
          <div className="mt-2">
            <Legend series={[{ name: 'New', color: 'var(--color-series-1)' }, { name: 'Expansion', color: 'var(--color-series-2)' }, { name: 'Contraction', color: 'var(--color-series-3)' }, { name: 'Churn', color: 'var(--color-bad)' }]} />
          </div>
        </Card>
        <Card title="Top customers by MRR">
          <HBars rows={r.topCustomers.slice(0, 8).map((t) => ({ label: t.name, value: t.mrr }))} format={(v) => money(v, c, { compact: true })} />
        </Card>
      </div>
      <Card className="mt-5" pad={false} title="MRR waterfall">
        <Table dense>
          <thead>
            <tr>
              <Th>Month</Th>
              <Th right>Starting</Th>
              <Th right>New</Th>
              <Th right>Expansion</Th>
              <Th right>Contraction</Th>
              <Th right>Churn</Th>
              <Th right>Ending MRR</Th>
              <Th right>ARR</Th>
              <Th right>Customers</Th>
            </tr>
          </thead>
          <tbody>
            {r.months.map((m) => (
              <tr key={m.period}>
                <Td>{period(m.period)}</Td>
                <Td right muted>
                  {acct(m.startingMrr, c, { cents: false })}
                </Td>
                <Td right className="text-good">
                  {acct(m.new, c, { cents: false })}
                </Td>
                <Td right className="text-good">
                  {acct(m.expansion, c, { cents: false })}
                </Td>
                <Td right className="text-warn">
                  {acct(m.contraction, c, { cents: false })}
                </Td>
                <Td right className="text-bad">
                  {acct(m.churn, c, { cents: false })}
                </Td>
                <Td right className="font-semibold">
                  {acct(m.endingMrr, c, { cents: false })}
                </Td>
                <Td right>{acct(m.arr, c, { cents: false })}</Td>
                <Td right>{m.customers}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </>
  );
}

function DeferredView({ r }: { r: DeferredRevenue }) {
  const c = r.currency;
  const fx = r.months.some((m) => m.translation !== 0);
  return (
    <>
      <ScopeNote consolidated={r.consolidated} currency={c} />
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Opening deferred" value={money(r.opening, c, { compact: true })} sub={period(r.fromPeriod)} />
        <Stat label="Billed" value={money(r.months.reduce((s, m) => s + m.billed, 0), c, { compact: true })} />
        <Stat label="Recognized" value={money(-r.months.reduce((s, m) => s + m.recognized, 0), c, { compact: true })} />
        <Stat label="Closing deferred" value={money(r.closing, c, { compact: true })} sub={period(r.toPeriod)} />
      </div>
      <div className="grid gap-5 xl:grid-cols-2">
        <Card title="Roll-forward">
          <BarChart
            labels={r.months.map((m) => period(m.period, true))}
            series={[
              { name: 'Billed', values: r.months.map((m) => m.billed), color: 'var(--color-series-2)' },
              { name: 'Recognized', values: r.months.map((m) => -m.recognized), color: 'var(--color-series-1)' },
            ]}
            format={(v) => money(v, c)}
            axisFormat={(v) => money(v, c, { compact: true })}
          />
          <Table dense className="mt-3">
            <thead>
              <tr>
                <Th>Month</Th>
                <Th right>Opening</Th>
                <Th right>Billed</Th>
                <Th right>Recognized</Th>
                {fx && <Th right>FX</Th>}
                <Th right>Closing</Th>
              </tr>
            </thead>
            <tbody>
              {r.months.map((m) => (
                <tr key={m.period}>
                  <Td>{period(m.period, true)}</Td>
                  <Td right muted>
                    {acct(m.opening, c, { cents: false })}
                  </Td>
                  <Td right>{acct(m.billed, c, { cents: false })}</Td>
                  <Td right>{acct(m.recognized, c, { cents: false })}</Td>
                  {fx && (
                    <Td right muted>
                      {acct(m.translation, c, { cents: false })}
                    </Td>
                  )}
                  <Td right className="font-semibold">
                    {acct(m.closing, c, { cents: false })}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
        <div className="space-y-5">
          <Card title="Remaining performance obligations" subtitle="Contracted revenue not yet recognized, by month it will be">
            <BarChart labels={r.remainingPerformanceObligations.map((x) => period(x.period, true))} series={[{ name: 'RPO', values: r.remainingPerformanceObligations.map((x) => x.amount), color: 'var(--color-brand-line)' }]} format={(v) => money(v, c)} axisFormat={(v) => money(v, c, { compact: true })} height={170} />
          </Card>
          <Card title="Deferred balance by customer">
            <HBars rows={r.byCustomer.slice(0, 8).map((x) => ({ label: x.name, value: x.balance }))} format={(v) => money(v, c, { compact: true })} />
          </Card>
        </div>
      </div>
    </>
  );
}

function RevenueByCustomerView({ r }: { r: RevenueByCustomer }) {
  const c = r.currency;
  const periods = [...new Set(r.rows.flatMap((x) => Object.keys(x.byPeriod)))].sort();
  return (
    <>
      <ScopeNote consolidated={r.consolidated} currency={c} />
      <Card pad={false} title={`Total ${money(r.total, c)}`} subtitle={r.rows[0] ? `Largest customer is ${pct(r.rows[0].share, 0)} of revenue` : undefined}>
        <Table dense>
          <thead>
            <tr>
              <Th>Customer</Th>
              {periods.map((p) => (
                <Th key={p} right>
                  {period(p, true)}
                </Th>
              ))}
              <Th right>Total</Th>
              <Th right>Share</Th>
            </tr>
          </thead>
          <tbody>
            {r.rows.map((x) => (
              <tr key={x.customerId} className="hover:bg-paper">
                <Td>
                  <Link to={`/ar/customers/${x.customerId}`} className="hover:text-brand hover:underline">
                    {x.name}
                  </Link>
                </Td>
                {periods.map((p) => (
                  <Td key={p} right muted>
                    {acct(x.byPeriod[p] ?? 0, c, { cents: false })}
                  </Td>
                ))}
                <Td right className="font-semibold">
                  {acct(x.total, c, { cents: false })}
                </Td>
                <Td right>{pct(x.share, 1)}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </>
  );
}

function AgingView({ r, kind }: { r: ArAging | ApAging; kind: 'customer' | 'vendor' }) {
  const c = r.currency;
  const [open, setOpen] = useState<string | null>(null);
  const label: Record<string, string> = { current: 'Current', '1_30': '1–30', '31_60': '31–60', '61_90': '61–90', '90_plus': '90+' };
  const totals = r.totals as Record<string, number>;
  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-6">
        <Stat label="Total open" value={money(r.total, c, { compact: true })} />
        {r.buckets.map((b) => (
          <Stat key={b} label={b === 'current' ? 'Current' : `${label[b]} days`} value={money(totals[b] ?? 0, c, { compact: true })} tone={b !== 'current' && (totals[b] ?? 0) > 0 ? 'bad' : undefined} />
        ))}
      </div>
      <Card pad={false}>
        <Table dense>
          <thead>
            <tr>
              <Th>{kind === 'customer' ? 'Customer' : 'Vendor'}</Th>
              {r.buckets.map((b) => (
                <Th key={b} right>
                  {label[b]}
                </Th>
              ))}
              <Th right>Total</Th>
            </tr>
          </thead>
          <tbody>
            {r.rows.map((x) => (
              <Fragment key={x.id}>
                <tr onClick={() => setOpen(open === x.id ? null : x.id)} className="cursor-pointer hover:bg-paper">
                  <Td>
                    <span className={cx('mr-1.5 inline-block text-faint transition-transform', open === x.id && 'rotate-90')}>
                      <Icons.Chevron size={12} />
                    </span>
                    {x.name}
                  </Td>
                  {r.buckets.map((b) => {
                    const v = (x.buckets as Record<string, number>)[b] ?? 0;
                    return (
                      <Td key={b} right className={cx(!v && 'text-faint', !!v && b !== 'current' && 'text-bad')}>
                        {acct(v, c)}
                      </Td>
                    );
                  })}
                  <Td right className="font-semibold">
                    {acct(x.total, c)}
                  </Td>
                </tr>
                {open === x.id &&
                  x.documents.map((d) => (
                    <tr key={d.id} className="bg-paper/60 text-[12.5px]">
                      <Td className="pl-9!">
                        <Link to={kind === 'customer' ? `/ar/invoices/${d.id}` : `/ap/bills/${d.id}`} className="text-brand hover:underline">
                          {d.number}
                        </Link>
                        <span className="ml-2 text-muted">
                          due {date(d.dueDate)}
                          {d.daysPastDue > 0 && ` · ${d.daysPastDue} days late`}
                        </span>
                      </Td>
                      {r.buckets.map((b) => (
                        <Td key={b} right muted>
                          {d.bucket === b ? acct(d.openBase, c) : ''}
                        </Td>
                      ))}
                      <Td right muted>
                        {d.currency !== c ? `${acct(d.open, d.currency)} ${d.currency}` : ''}
                      </Td>
                    </tr>
                  ))}
              </Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-ink font-semibold">
              <Td>Total</Td>
              {r.buckets.map((b) => (
                <Td key={b} right>
                  {acct(totals[b] ?? 0, c)}
                </Td>
              ))}
              <Td right>{acct(r.total, c)}</Td>
            </tr>
          </tfoot>
        </Table>
      </Card>
    </>
  );
}

function SpendView({ r }: { r: Spend }) {
  const c = r.currency;
  const periods = [...new Set(r.rows.flatMap((x) => Object.keys(x.byPeriod)))].sort();
  return (
    <>
      <ScopeNote consolidated={r.consolidated} currency={c} />
      <div className="grid gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
        <Card title={`Total ${money(r.total, c, { compact: true })}`} subtitle={`By ${r.groupBy}`}>
          <HBars rows={r.rows.slice(0, 10).map((x) => ({ label: x.key, value: x.total, sub: pct(r.total ? x.total / r.total : 0, 0) }))} format={(v) => money(v, c, { compact: true })} color="var(--color-series-3)" />
        </Card>
        <Card pad={false}>
          <Table dense>
            <thead>
              <tr>
                <Th>{r.groupBy === 'vendor' ? 'Vendor' : r.groupBy === 'department' ? 'Department' : 'Account'}</Th>
                {periods.map((p) => (
                  <Th key={p} right>
                    {period(p, true)}
                  </Th>
                ))}
                <Th right>Total</Th>
              </tr>
            </thead>
            <tbody>
              {r.rows.map((x) => (
                <tr key={x.key} className="hover:bg-paper">
                  <Td>{x.key}</Td>
                  {periods.map((p) => (
                    <Td key={p} right muted>
                      {acct(x.byPeriod[p] ?? 0, c, { cents: false })}
                    </Td>
                  ))}
                  <Td right className="font-semibold">
                    {acct(x.total, c, { cents: false })}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      </div>
    </>
  );
}

// --- Cash ---------------------------------------------------------------------------------------------------------

function ForecastView({ r }: { r: CashForecast }) {
  const c = r.currency;
  const labels = r.weeks.map((w) => `W${w.week}`);
  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Cash today" value={money(r.openingCash, c, { compact: true })} sub={`As of ${date(r.asOf)}`} />
        <Stat label="In 13 weeks" value={money(r.endingCash, c, { compact: true })} tone={r.endingCash < r.openingCash ? 'warn' : 'good'} sub={`${money(r.endingCash - r.openingCash, c, { compact: true, sign: true })} net`} />
        <Stat label="Lowest point" value={money(r.lowestPoint.cash, c, { compact: true })} sub={`Week ${r.lowestPoint.week}`} tone={r.lowestPoint.cash < 0 ? 'bad' : undefined} />
        <Stat label="Weekly run-rate spend" value={money(r.assumptions.weeklyRecurringSpend, c, { compact: true })} sub="Trailing 90 days" />
      </div>
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
        <Card title="Projected cash balance" subtitle="Weekly: expected collections (probability-weighted by lateness), scheduled billings, approved payables and run-rate spend">
          <LineChart labels={labels} series={[{ name: 'Ending cash', values: r.weeks.map((w) => w.endingCash), color: 'var(--color-series-1)' }]} area format={(v) => money(v, c)} axisFormat={(v) => money(v, c, { compact: true })} height={230} />
          <div className="mt-4">
            <BarChart
              labels={labels}
              stacked
              series={[
                { name: 'Collections', values: r.weeks.map((w) => w.collections), color: 'var(--color-series-1)' },
                { name: 'New billings', values: r.weeks.map((w) => w.scheduledBillings), color: 'var(--color-series-2)' },
                { name: 'Payables', values: r.weeks.map((w) => w.payables), color: 'var(--color-series-3)' },
                { name: 'Run-rate spend', values: r.weeks.map((w) => w.recurringSpend), color: 'var(--color-series-5)' },
              ]}
              format={(v) => money(v, c)}
              axisFormat={(v) => money(v, c, { compact: true })}
              height={170}
            />
            <div className="mt-2">
              <Legend series={[{ name: 'Collections', color: 'var(--color-series-1)' }, { name: 'New billings', color: 'var(--color-series-2)' }, { name: 'Payables', color: 'var(--color-series-3)' }, { name: 'Run-rate spend', color: 'var(--color-series-5)' }]} />
            </div>
          </div>
        </Card>
        <Card title="Assumptions">
          <div className="text-[12px] font-medium text-ink-2">Probability of collecting an invoice</div>
          <table className="mt-1.5 w-full text-[12.5px]">
            <tbody>
              {r.assumptions.collectionProbability.map((x) => (
                <tr key={x.daysLate}>
                  <td className="py-0.5 text-muted">{x.daysLate === '<=0' ? 'Not yet due' : `${x.daysLate} days late`}</td>
                  <td className="num py-0.5 text-right">{pct(x.probability, 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-[12px] text-muted">{r.assumptions.recurringSpendBasis}</p>
        </Card>
      </div>
      <Card className="mt-5" pad={false}>
        <Table dense>
          <thead>
            <tr>
              <Th>Week</Th>
              <Th right>Collections</Th>
              <Th right>New billings</Th>
              <Th right>Payables</Th>
              <Th right>Run-rate spend</Th>
              <Th right>Net</Th>
              <Th right>Ending cash</Th>
            </tr>
          </thead>
          <tbody>
            {r.weeks.map((w) => (
              <tr key={w.week}>
                <Td>
                  W{w.week} <span className="text-muted">· {date(w.weekStart, { year: false })} – {date(w.weekEnd, { year: false })}</span>
                </Td>
                <Td right>{acct(w.collections, c, { cents: false })}</Td>
                <Td right>{acct(w.scheduledBillings, c, { cents: false })}</Td>
                <Td right>{acct(w.payables, c, { cents: false })}</Td>
                <Td right>{acct(w.recurringSpend, c, { cents: false })}</Td>
                <Td right className={w.net < 0 ? 'text-bad' : 'text-good'}>
                  {acct(w.net, c, { cents: false })}
                </Td>
                <Td right className="font-semibold">
                  {acct(w.endingCash, c, { cents: false })}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </>
  );
}

function BurnView({ r }: { r: BurnRunway }) {
  const c = r.currency;
  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Cash" value={money(r.cash, c, { compact: true })} />
        <Stat label="Average net burn" value={money(r.averageMonthlyBurn, c, { compact: true })} sub="Per month" />
        <Stat label="Runway" value={r.runwayMonths === null ? '∞' : `${num(r.runwayMonths, 1)} months`} tone={r.runwayMonths !== null && r.runwayMonths < 12 ? 'bad' : 'good'} />
        <Stat label="Cash-flow positive" value={r.cashFlowPositive ? 'Yes' : 'Not yet'} tone={r.cashFlowPositive ? 'good' : undefined} />
      </div>
      <div className="grid gap-5 xl:grid-cols-2">
        <Card title="Ending cash">
          <LineChart labels={r.months.map((m) => period(m.period, true))} series={[{ name: 'Ending cash', values: r.months.map((m) => m.endingCash) }]} area format={(v) => money(v, c)} axisFormat={(v) => money(v, c, { compact: true })} />
        </Card>
        <Card title="Inflows and outflows">
          <BarChart
            labels={r.months.map((m) => period(m.period, true))}
            series={[
              { name: 'Inflows', values: r.months.map((m) => m.inflows), color: 'var(--color-series-1)' },
              { name: 'Outflows', values: r.months.map((m) => m.outflows), color: 'var(--color-series-3)' },
            ]}
            format={(v) => money(v, c)}
            axisFormat={(v) => money(v, c, { compact: true })}
          />
        </Card>
      </div>
    </>
  );
}

function PositionView({ r }: { r: CashPosition }) {
  const c = r.currency;
  return (
    <Card title={`Total cash ${money(r.total, c)}`} subtitle={`As of ${date(r.asOf)}`}>
      <HBars rows={r.accounts.map((a) => ({ label: `${a.code} · ${a.name}`, value: a.balance }))} format={(v) => money(v, c)} />
    </Card>
  );
}

// --- CSV export ---------------------------------------------------------------------------------------------------

function downloadCsv(container: HTMLElement, filename: string) {
  const tables = [...container.querySelectorAll('table')];
  const rows: string[] = [];
  for (const t of tables) {
    for (const tr of t.querySelectorAll('tr')) {
      const cells = [...tr.querySelectorAll('th,td')].map((c) => `"${(c.textContent ?? '').replace(/\s+/g, ' ').trim().replace(/"/g, '""')}"`);
      if (cells.some((x) => x !== '""')) rows.push(cells.join(','));
    }
    rows.push('');
  }
  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}
